import React, { useState, useRef, forwardRef } from 'react';
import { Box, Typography } from '@mui/material';
import { IKeyPoint } from './constants';
import { SingleInput } from './SingleInput';
import { SingleLineEntry } from './singleLineEntry';
import * as Reorder from '../../../../components/dnd/Reorder';
import { useMergePendingItems, useMergePendingItemsHandle } from '../../../../hooks/useMergePendingItems';

interface Props {
  keyPoints: Array<IKeyPoint>;
  updateKeyPoints: (keyPoints: Array<IKeyPoint>) => void;
}

export default forwardRef<{ mergePendingItems: () => IKeyPoint[] }, Props>(
  function FormKeyPoints({ keyPoints, updateKeyPoints }, ref) {
    const [editableIndex, setEditableIndex] = useState(-1);
    const inputRef = useRef<{ getCurrentValue: () => string; clear: () => void }>(null);

    const { pendingItems, mergePendingItems, addToPending, clearPending } = useMergePendingItems<IKeyPoint, string>({
      items: keyPoints,
      updateItems: updateKeyPoints,
      inputRef,
      transformItem: (item) => ({ keyPoint: item as string }),
      mergeOrder: 'prepend',
    });

    useMergePendingItemsHandle(ref, mergePendingItems);

    const isValidKeyPoint = (text: string) => text?.trim()?.length;

    const handleAddToPending = (keypoint: string) => {
      if (isValidKeyPoint(keypoint)) {
        addToPending(keypoint);
      }
    };

    const addSingleKeyPoint = (keypoint: string) => {
      if (isValidKeyPoint(keypoint)) {
        const allItems = [...pendingItems, keypoint];
        updateKeyPoints([...allItems.map(item => ({ keyPoint: item })), ...keyPoints]);
        clearPending();
      }
      setEditableIndex(-1);
    };

  const updateSingleKeyPoint = (keypoint: string, index: number) => {
    const tempList = [...keyPoints];
    tempList[index] = { ...tempList[index], keyPoint: keypoint };
    updateKeyPoints([...tempList]);
    setEditableIndex(-1);
  };

  const removeSingleKeyPoint = (index: number) => {
    const tempList = [...keyPoints];
    tempList.splice(index, 1);
    updateKeyPoints([...tempList]);
  };

  const props = { label: 'Key Point' };
  return (
    <Box className="flex flex-col gap-1 w-full">
      <SingleInput 
        ref={inputRef}
        {...props} 
        onTextChange={addSingleKeyPoint}
        onAddToPending={handleAddToPending}
      />

      {pendingItems.length > 0 && (
        <Box className="flex flex-col gap-2 w-full mb-2">
          {pendingItems.map((item, index) => (
            <Box key={`pending-${index}`} className="p-2 border border-dashed border-border-token rounded-lg bg-surface-2">
              <Typography className="text-text-1">{item}</Typography>
            </Box>
          ))}
        </Box>
      )}

      <Reorder.Root options={keyPoints} onUpdateOrder={updateKeyPoints}>
        {keyPoints.map((item, index) => {
          if (editableIndex === index) {
            return (
              <SingleInput
                {...props}
                key={`${item.keyPoint}-${index}`}
                text={item.keyPoint}
                onTextChange={(text) => updateSingleKeyPoint(text, index)}
                isEditMode={true}
              />
            );
          }

          return (
            <SingleLineEntry
              index={index}
              text={item.keyPoint}
              key={`${item.keyPoint}-${index}`}
              editText={() => setEditableIndex(index)}
              removeText={() => removeSingleKeyPoint(index)}
            />
          );
        })}
      </Reorder.Root>
    </Box>
  );
});
